import * as React from "react";
import { FixedCommentMutation, TestAcceptabilityMutation, OverreadStatusEnum, ModelError, Permission, SessionMetadata, OverreadStatusIdEnum, OverreadChallengeStatusEnum } from "./../misc/api/apiTypes";
import { LungVolumeResultsTable } from "./LungVolumeResultsTable";
import { RouteComponentProps } from "react-router";
import { useFixedComments, ToggleSessionFixedCommentsVisibilityAction } from "./Shared/UseFixedComments";
import FileAttachmentsTable from "./../file-attachments/components/viewer/FileAttachmentsTable";
import { FileAttachmentsTableMode } from "./../file-attachments/models";
import NotesPanel from "./Shared/NotesPanel";
import LoadingWithMessage from "./../misc/components/LoadingWithMessage";
import Alert, { AlertCategoryEnum } from "./../misc/components/Alert";
import SessionMetadataTable from "./Shared/SessionMetadataTable";
import AcceptabilityDashboard from "./Shared/AcceptabilityDashboard";
import { useAcceptability, LoadingBestValuesAction, UpdateBestTestAction, UpdateRepeatabilityAction, DoneLoadingBestValuesAction } from './Shared/UseAcceptability';
import { LungVolumeAnalysisSetTypeEnum } from "../misc/api/ApiLungVolumeAnalysisSetType";
import { PermissionEnum } from "./../misc/api/ApiPermissionEnum";
import { SessionTypes } from "./../misc/api/ApiSessionTypes";
import { SessionNote, WebPortalClient } from "webportalclient";
import { PFTResults, LungVolumeResults, SpirometryResults, StudyLungVolumeRelevantParameters } from "./Shared/PFTAPITypes";
import { useSecurityContext } from "../Core/SecurityContext";
import { TestQualityGrade } from "./Shared/TestQualityGrading";
import { ChallengeAlert } from "./Shared/ChallengeAlert";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../misc/api/WebPortalTokenClient";
import { GoBackToList } from "./Shared/AnalysisHelpers";


export interface LungVolumeAnalysisScreenProps {
    currentPermissions: Permission[];
}

export interface LungVolumeAnalysisScreenRouteProps
    extends RouteComponentProps<ExpectedMatchParams>,
    LungVolumeAnalysisScreenProps { }

enum ViewState {
    View = "view",
    Overread = "overread"
}

interface ExpectedMatchParams {
    analysisSetId: string;
    viewState: ViewState;
}

export const LungVolumeAnalysisScreen = ({ match, history }: LungVolumeAnalysisScreenRouteProps) => {
    const client = useWebPortalApiClient();
    const legacyClient = useWebPortalLegacyClient();
    const { analysisSetId, viewState } = match.params;
    const { currentUser, currentUserPermissions } = useSecurityContext();
    const [sessionMetadata, setSessionMetadata] = React.useState<SessionMetadata>(null);
    const [sessionType, setSessionType] = React.useState<SessionTypes>(null);
    const [analysisSet, setAnalysisSet] = React.useState<LungVolumeResults>(null);
    const [sessionNotes, setSessionNotes] = React.useState<SessionNote[]>(null);
    const [isViewOnly, setIsViewOnly] = React.useState<boolean>(true);
    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
    const [isAnalysisRejected, setIsAnalysisRejected] = React.useState<boolean>(false);
    const [studyLungVolumeRelevantParameters, setStudyLungVolumeRelevantParameters] = React.useState<StudyLungVolumeRelevantParameters>(null);
    const [error, setError] = React.useState<string | undefined>();
    const [grades, setGrades] = React.useState({
        fev1: TestQualityGrade.none,
        fvc: TestQualityGrade.none,
    });

    const fetchSessionNotes = async (sessionId: string) => {
        const notes = await client.client.getSessionNotes(undefined, `sessionId eq ${sessionId}`);
        setSessionNotes(notes.value);
    }

    React.useEffect(() => {
        const fetchAnalysisSet = async () => {
            if (viewState === ViewState.Overread) {
                setIsViewOnly(false);
                await legacyClient.startOverreadingLungVolume(analysisSetId);
            } else {
                setIsViewOnly(true);
            }

            const results = await legacyClient.getLungVolumeAnalysisSetResults(analysisSetId);
            setAnalysisSet(results);

            const sessionMetadata = await legacyClient.getLungVolumeSession(results.SessionId);
            setSessionMetadata(sessionMetadata);

            if (sessionMetadata.SessionTypeId === SessionTypes.LungVolumeHelium) {
                setSessionType(SessionTypes.LungVolumeHelium);
                const parameters = await legacyClient.getStudyLungVolumeHeliumRelevantParameters();
                
                setStudyLungVolumeRelevantParameters(parameters);
            }
            else {
                setSessionType(SessionTypes.LungVolumePleth);
                const parameters = await legacyClient.getStudyLungVolumePlethRelevantParameters();
                
                setStudyLungVolumeRelevantParameters(parameters);
            }
              
            const isRejected = sessionMetadata.AnalysisSets.find(a => a.Id === analysisSetId).Status.Id === OverreadStatusIdEnum.Rejected;
            setIsAnalysisRejected(isRejected);

            await fetchSessionNotes(results.SessionId);
        }

        fetchAnalysisSet();
    }, [analysisSetId, viewState]);

    const [shouldValidateFixedComments, setShouldValidateFixedComments] = React.useState(false);

    const [fixedCommentState, fixedCommentDispatch] = useFixedComments(analysisSet, sessionType);
    const [acceptabilityState, acceptabilityDispatch] = useAcceptability(analysisSet);

    React.useEffect(() => {

        const updateBestTestAndRepeatability = async () => {
            if (acceptabilityState.spirometryResults.TestResults.length) {

                acceptabilityDispatch(LoadingBestValuesAction.create());

                const lungVolumeRepeatabilityValues = await legacyClient.calculateLungVolumeRepeatability(sessionMetadata.Id, acceptabilityState.spirometryResults.TestResults);
                const lungVolumeBestTestValues = sessionType === SessionTypes.LungVolumeHelium ? await legacyClient.calculateLungVolumeHeBestTests(acceptabilityState.spirometryResults.TestResults) :
                    await legacyClient.calculateLungVolumePlethBestTests(acceptabilityState.spirometryResults.TestResults);
                
                acceptabilityDispatch(UpdateBestTestAction.create(lungVolumeBestTestValues));
                acceptabilityDispatch(UpdateRepeatabilityAction.create(lungVolumeRepeatabilityValues));

                acceptabilityDispatch(DoneLoadingBestValuesAction.create());
            }
        }
        updateBestTestAndRepeatability();
    }, [acceptabilityState.changedAcceptability, acceptabilityDispatch]);

    if (analysisSet === null || sessionMetadata === null || studyLungVolumeRelevantParameters === null || sessionNotes == null) {
        return (<LoadingWithMessage message="Loading Lung Volume Data..." />);
    }

    const goBackToList = () => GoBackToList(history, currentUserPermissions, "/LungVolume", PermissionEnum.OverreadLungVolumeSession, "/LungVolume/Analysis");

    const setAnalysisDecision = async (annotation: string, decision: OverreadStatusEnum) => {
        setIsSubmitting(true);
        setError(undefined);

        const sessionFixedCommentsMutation = fixedCommentState.sessionFixedComments.comments.map(x => ({ CommentId: x.id, IsSelected: x.selected } as FixedCommentMutation));
        const testFixedCommentsMutation = fixedCommentState.testFixedComments.map(x => ({
            TestId: x.id, Comments: x.comments.map(c => ({ CommentId: c.id, IsSelected: c.selected } as FixedCommentMutation)),
        }));
        const testAcceptability = acceptabilityState.spirometryResults.TestResults.map(x => ({ TestId: x.TestId, Accepted: x.OverreaderAcceptability ? true : false } as TestAcceptabilityMutation));

        try {
            const response = await legacyClient.updateAcceptabilityLungVolume(analysisSetId, decision, testAcceptability, sessionFixedCommentsMutation, testFixedCommentsMutation, annotation);

            if (response.AnalysisSetId !== analysisSetId) {
                setError("An error occurred saving the analysis set. Please contact an Administrator.");
            }

            goBackToList();
        }
        catch (ex) {
            setError(ex.Message);
            window.scrollTo(0, 0);
        }
    }

    const addNewNote = async (annotation: string) => {
        await legacyClient.putSessionNotes(analysisSet.SessionId, annotation);
        await fetchSessionNotes(analysisSet.SessionId);
    }

    const overreadingDisabled = viewState === "overread" && (fixedCommentState.areAvailableFixedCommentsLoaded === false || (fixedCommentState.areFixedCommentsLoaded === undefined || fixedCommentState.areFixedCommentsLoaded === null || fixedCommentState.areFixedCommentsLoaded === false));

    const hasAddingNotePermission = currentUserPermissions.some(x=>x.Id == PermissionEnum.AddLungVolumeSessionNotes);
    const hasViewNotePermission = currentUserPermissions.some(x => x.Id == PermissionEnum.ViewLungVolumeSessionNotes);

    const isQCAnalysisSet = sessionMetadata.AnalysisSets.find(x => x.Id === match.params.analysisSetId).Type.Id === LungVolumeAnalysisSetTypeEnum.QC;
    const isSiteAnalysisSet = sessionMetadata.AnalysisSets.find(x => x.Id === match.params.analysisSetId).Type.Id === LungVolumeAnalysisSetTypeEnum.Site;

    return (
        <>
            <Alert show={overreadingDisabled} category={AlertCategoryEnum.Danger}>
                Over-reading disabled. Fixed comments have not been configured for this study, please ask the Administrator to configure these first.
            </Alert>

            <Alert show={error !== undefined} category={AlertCategoryEnum.Danger}>
                {error}
            </Alert>

            {!isQCAnalysisSet && !isSiteAnalysisSet && !isViewOnly && sessionMetadata.ChallengeStatusId === OverreadChallengeStatusEnum.Challenged && !sessionMetadata.ChallengeIsClosed ?
                <ChallengeAlert
                    sessionId={sessionMetadata.Id}
                    sessionType={sessionType}
                    onRejectComplete={goBackToList}
                /> : null}

            <h2 className="text-primary">Lung Volume Data</h2>
            {!isQCAnalysisSet ? <SessionMetadataTable
                sessionMetadata={sessionMetadata}
                selectedAnalysisSetId={analysisSetId}
            /> : <Alert show={true} category={AlertCategoryEnum.Info}>Session Information is hidden from view in this workflow</Alert>}

            <h2 className="text-primary">Results</h2>
            <div className="panel panel-default">
                <div className="panel-body">
                    <LungVolumeResultsTable
                        isRejected={isAnalysisRejected}
                        isViewOnly={isViewOnly || overreadingDisabled || acceptabilityState.loadingBestValues}
                        isSiteAnalysisSet={isSiteAnalysisSet}
                        resultData={acceptabilityState.spirometryResults as SpirometryResults}
                        fixedCommentsState={fixedCommentState}
                        fixedCommentsDispatch={fixedCommentDispatch}
                        shouldValidateFixedComments={shouldValidateFixedComments}
                        acceptabilityState={acceptabilityState}
                        acceptabilityDispatch={acceptabilityDispatch}
                        grades={grades}
                        studyLungVolumeRelevantParameters={studyLungVolumeRelevantParameters}
                    />
                </div>
            </div>
            <h2 className="text-primary">Attachments</h2>
            <div className="panel panel-default">
                <div className="panel-body">
                    <FileAttachmentsTable
                        legacyClient={legacyClient}
                        fileAttachments={analysisSet.Attachments.map(a => ({ id: a.Id, fileSizeInBytes: a.FileSizeInBytes, fileName: a.FileName }))}
                        mode={FileAttachmentsTableMode.ListOnly}
                    />
                </div>
            </div>
            {
                hasViewNotePermission? 
                <NotesPanel header="Session Notes" isAddingNotesEnabled={!isViewOnly && !overreadingDisabled && hasAddingNotePermission} notes={sessionNotes} onAddNote={(annotation) => addNewNote(annotation)} />
                : null
            }
            <AcceptabilityDashboard
                isViewOnly={isViewOnly || overreadingDisabled}
                analysisSetId={analysisSetId}
                fixedCommentsDispatch={fixedCommentDispatch}
                fixedCommentState={fixedCommentState}
                acceptabilityState={acceptabilityState}
                sessionType={sessionType}
                overreadStatus={sessionMetadata.AnalysisSets.find(a => a.Id === analysisSetId).Status.Id}
                onNoSessionCommentsSelected={() => {
                    fixedCommentDispatch(ToggleSessionFixedCommentsVisibilityAction.create());
                }}
                onAcceptabilitySelected={(annotation, decision) => setAnalysisDecision(annotation, decision)}
                onCancel={goBackToList}
            />
        </>
    );
};

export default LungVolumeAnalysisScreen;
